import React from 'react';
import { Modal, Form, Input, Button, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';

interface ChangePasswordModalProps {
  visible: boolean;
  onCancel: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ visible, onCancel }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      await authService.changePassword({
        old_password: values.oldPassword,
        new_password: values.newPassword,
        new_password_confirmation: values.confirmPassword,
      });
      
      message.success(t('auth.passwordChangedSuccessfully'));
      form.resetFields();
      onCancel();
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        Object.keys(errors).forEach(key => {
          message.error(errors[key][0]);
        });
      } else {
        message.error(t('auth.passwordChangeFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={t('auth.changePassword')}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={400}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Form.Item
          name="oldPassword"
          label={t('auth.currentPassword')}
          rules={[
            { required: true, message: t('auth.currentPasswordRequired') }
          ]}
        >
          <Input.Password 
            placeholder={t('auth.enterCurrentPassword')}
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label={t('auth.newPassword')}
          rules={[
            { required: true, message: t('auth.newPasswordRequired') },
            { min: 8, message: t('auth.passwordMinLength') }
          ]}
        >
          <Input.Password 
            placeholder={t('auth.enterNewPassword')}
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label={t('auth.confirmNewPassword')}
          dependencies={['newPassword']}
          rules={[
            { required: true, message: t('auth.confirmPasswordRequired') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('auth.passwordsDoNotMatch')));
              },
            }),
          ]}
        >
          <Input.Password 
            placeholder={t('auth.confirmNewPassword')}
            size="large"
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button 
            type="default" 
            onClick={handleCancel}
            style={{ marginRight: 8 }}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            type="primary" 
            htmlType="submit"
            loading={loading}
          >
            {t('auth.changePassword')}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal; 